/**
 * @changelog
 *
 * 2026-03-11 (0.1.0) (jhoward)
 *   - Add App\Helpers\getAccountInfo() to fetch and cache account info using peppermillCustomFetchProxy.
 *   - Returns OpcAccountInfo or null based on user state and cache.
 *
 * 2026-04-09 (0.1.1) (jhoward)
 *   - Add new_pin_required to AccountInfoResponse and update appStore state accordingly.
 *
 * 2026-10-10 (0.2.0) (jhoward)
 *   - Add isRevalidating, for a page that is cached by <KeepAlive> and is refreshing what it already shows: it skips the
 *     store copy and fetches without the loading spinner.
 *
 * 2026-10-15 (0.2.1) (jhoward)
 *   - Only store and return account info from a 200 that has it. A failed refresh answers with data but no
 *     account_info, and that blanked the Home card and the whole Account page.
 */

import type { FetchResponse } from '@framework/defs/framework';
import type { OpcAccountInfo } from '@/defs/api/Accountinfo';
import type { OpcTournamentResult } from '@/defs/api/TournamentResult';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import useAppStore from '@/store';
import { view } from '@rss/routes/account/info';

interface AccountInfoResponse extends FetchResponse {
  data?: {
    account_info: OpcAccountInfo;
    account_info_from_cache: boolean;
    tournament_results: OpcTournamentResult[];
    tournament_results_from_cache: boolean;
    new_pin_required: boolean;
    account_reverify_required: boolean;
  };
}

export default async function getAccountInfo(isRevalidating = false): Promise<{
  accountInfo: OpcAccountInfo;
  tournamentResults: OpcTournamentResult[];
} | null> {
  const appStore = useAppStore();
  if (!appStore.state.user) {
    return null;
  }

  if (appStore.account.info && !isRevalidating) {
    return { accountInfo: appStore.account.info, tournamentResults: appStore.account.tournamentResults };
  }

  const response = await peppermillCustomFetchProxy<AccountInfoResponse>(
    {},
    {
      route: view(),
      showLoadingSpinner: !isRevalidating,
    },
  );
  if (response.data) {
    appStore.state.new_pin_required = response.data.new_pin_required === true;
  }

  //A failure still carries data (the PIN and reverify flags) but no account_info; keep what is already shown.
  if (200 === response.status && response.data?.account_info) {
    appStore.account.info = response.data.account_info;
    appStore.account.tournamentResults = response.data.tournament_results;
    return { accountInfo: response.data.account_info, tournamentResults: response.data.tournament_results };
  }

  return null;
}
